import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/hooks/useAuth";
import { consumeLaunchToken } from "@/lib/integrationClient";

export default function LaunchFromPulse() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [status, setStatus] = useState<"idle" | "consuming" | "error">("idle");
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    if (loading) return;
    const token = params.get("token") ?? "";
    if (!token) { setStatus("error"); setMessage("Missing launch token."); return; }
    if (!user) {
      const next = `/launch?token=${encodeURIComponent(token)}`;
      navigate(`/auth?next=${encodeURIComponent(next)}`, { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      setStatus("consuming");
      try {
        const res = await consumeLaunchToken(token);
        if (cancelled) return;
        navigate(`/?scenario=${res.scenario_id}&from=pulse`, { replace: true });
      } catch (e: unknown) {
        if (cancelled) return;
        setStatus("error");
        setMessage(e instanceof Error ? e.message : "Could not open Pulse launch.");
      }
    })();
    return () => { cancelled = true; };
  }, [loading, user, params, navigate]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-8">
      <Helmet><title>Opening prefilled plan — Family Loan Planner</title></Helmet>
      <div className="max-w-md text-center space-y-3">
        <h1 className="text-lg font-semibold">Opening prefilled loan plan…</h1>
        {status === "consuming" && <p className="text-sm text-muted-foreground">Verifying and preparing your draft.</p>}
        {status === "error" && (
          <>
            <p className="text-sm text-destructive">{message}</p>
            <p className="text-xs text-muted-foreground">Ask the sender to generate a new launch link.</p>
          </>
        )}
      </div>
    </div>
  );
}